import { Button, type ButtonProps } from '@mui/material'
import difference from 'lodash/difference'
import { useFormContext, useWatch } from 'react-hook-form'

import { isObservabilityToolOfType } from 'components/status-checks/authentication-form-validation-utils'
import { type StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'
import { useStatusCheckDetailsContext } from 'context/status-checks/StatusCheckDetailsContext'
import { ObservabilityToolType } from 'types/api/status-checks/status-check'

/**
 * Hooks into Create/Edit Status Check forms to determine form validity and provides
 * an action to submit the form.
 */
export const SaveStatusCheckFormButton: React.FC<
  ButtonProps & {
    dirtyFieldOverrides?: (keyof StatusCheckFormValues)[]
    label: string
  }
> = ({ dirtyFieldOverrides, label, ...buttonAttributes }) => {
  const { isStatusCheckConnectionValidated, isStatusCheckEvaluationValidated } =
    useStatusCheckDetailsContext()

  const { control, formState } = useFormContext<StatusCheckFormValues>()
  const { dirtyFields, isDirty, isSubmitting, isValid } = formState
  const dirtyFieldNames = Object.keys(dirtyFields)
  const shouldOverrideDirtyFields =
    dirtyFieldOverrides &&
    isDirty &&
    !difference(dirtyFieldNames, dirtyFieldOverrides).length

  const observabilityTool = useWatch({
    control,
    name: 'observabilityTool'
  })

  const isCustomObservabilityTool = isObservabilityToolOfType(
    ObservabilityToolType.CUSTOM,
    observabilityTool
  )
  const isPrometheusObservabilityTool = isObservabilityToolOfType(
    ObservabilityToolType.PROMETHEUS,
    observabilityTool
  )

  /*
   * Should only validate the Success Evaluation when the observability tool type is custom.
   * Otherwise, all supported o11y integration evaluation configurations are generated by the GCP.
   */
  const isSuccessEvaluationValid =
    isCustomObservabilityTool || isPrometheusObservabilityTool
      ? isStatusCheckEvaluationValidated
      : true

  const isEnabled =
    shouldOverrideDirtyFields ||
    (isStatusCheckConnectionValidated &&
      isSuccessEvaluationValid &&
      isValid &&
      !isSubmitting)

  return (
    <Button
      {...buttonAttributes}
      disabled={!isEnabled || isSubmitting}
      type="submit"
      variant="contained"
    >
      {label}
    </Button>
  )
}
